import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import './LoginImageCarousel.css';

interface LoginImage {
  id: number;
  url: string;
  alt: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LoginImageCarouselProps {
  className?: string;
}

const LoginImageCarousel: React.FC<LoginImageCarouselProps> = ({ className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousIndex, setPreviousIndex] = useState(0);

  // Buscar imagens da API
  const { data: images = [], isLoading } = useQuery<LoginImage[]>({
    queryKey: ['login-carousel-images'],
    queryFn: async () => {
      const response = await fetch('/api/login-carousel/images');
      if (!response.ok) throw new Error('Erro ao carregar imagens');
      const result = await response.json();
      return result.data;
    }
  });

  useEffect(() => {
    if (images.length === 0) return;

    // Auto-rotacionar a cada 5 segundos
    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  const startTransition = (newIndex: number) => {
    if (isTransitioning || newIndex === currentIndex) return;
    
    setIsTransitioning(true);
    setPreviousIndex(currentIndex);
    setCurrentIndex(newIndex);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800); // Match CSS transition duration
  };

  const goToPrevious = () => {
    if (images.length === 0) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    startTransition(newIndex);
  };

  const goToNext = () => {
    if (images.length === 0) return;
    const newIndex = (currentIndex + 1) % images.length;
    startTransition(newIndex);
  };

  if (isLoading) {
    return (
      <div className={`bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center ${className}`}>
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    // Imagem padrão com o logo da Átomos
    return (
      <div className={`bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center relative overflow-hidden ${className}`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute top-32 right-20 w-24 h-24 bg-white rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-white rounded-full"></div>
          <div className="absolute bottom-32 right-1/3 w-16 h-16 bg-white rounded-full"></div>
        </div>
        
        {/* Logo Átomos */}
        <div className="text-center text-white z-10">
          <div className="w-24 h-24 mx-auto mb-6 bg-white rounded-full flex items-center justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2">Portal Átomos</h1>
          <p className="text-lg opacity-90">Solução e Gestão</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Container das imagens com transições */}
      <div className="relative w-full h-full">
        {/* Renderizar todas as imagens para transições suaves */}
        {images.map((image, index) => (
          <div
            key={`${image.id}-${index}`}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentIndex
                ? 'opacity-100 scale-100 z-10'
                : index === previousIndex && isTransitioning
                ? 'opacity-0 scale-105 z-0'
                : 'opacity-0 scale-95 z-0'
            }`}
          >
            <img
              src={image.url}
              alt={image.alt || 'Imagem do carrossel'}
              className={`w-full h-full object-cover ${
                index === currentIndex ? 'ken-burns' : ''
              }`}
              onError={(e) => {
                // Fallback para imagem padrão em caso de erro
                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImciIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIG9mZnNldD0iMCUiIHN0b3AtY29sb3I9IiM2MzY2ZjEiLz48c3RvcCBvZmZzZXQ9IjEwMCUiIHN0b3AtY29sb3I9IiM4YjVjZjYiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2cpIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSI0OCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Qb3J0YWwgw4F0b21vczwvdGV4dD48L3N2Zz4=';
              }}
            />
            
            {/* Overlay gradiente com animação sutil */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-black/10 to-transparent"></div>
            
            {/* Overlay adicional para transições */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent transition-opacity duration-700 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}></div>
          </div>
        ))}
        
        
        {/* Efeito de partículas sutil */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full floating-particle"></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white/30 rounded-full floating-particle"></div>
          <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-white/25 rounded-full floating-particle"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1 h-1 bg-white/20 rounded-full floating-particle"></div>
        </div>
      </div>

      {/* Controles de navegação */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            disabled={isTransitioning}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full carousel-control disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Imagem anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={goToNext}
            disabled={isTransitioning}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full carousel-control disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Próxima imagem"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Indicadores de posição */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => startTransition(index)}
              disabled={isTransitioning}
              className={`relative carousel-indicator disabled:cursor-not-allowed ${
                index === currentIndex 
                  ? 'w-8 h-3 active' 
                  : 'w-3 h-3'
              }`}
              aria-label={`Ir para imagem ${index + 1}`}
            >
              <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white shadow-lg' 
                  : 'bg-white/50 hover:bg-white/75'
              }`} />
              {index === currentIndex && (
                <div className="absolute inset-0 bg-white rounded-full animate-pulse opacity-30" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LoginImageCarousel;
